/**
 * Routes for the components. Corresponds to the "View Controller".
 * 	@author Adrian Rebmann, Yannik Sprengart
 */

import Vue from "vue";
import Router from "vue-router";
import auth from "../auth";

import kircLanding from "@/components/company/kirc/landing/kircLanding.vue";
import Landing from "@/components/landing/Landing.vue";
import News from "@/components/landing/news/News.vue";
import Settings from "@/components/app/settings/Settings.vue";
import BusinessModel from "@/components/company/businessmodel/BusinessModel.vue";
import Brick from "@/components/company/businessmodel/tile/Brick.vue";
import BMMenu from "@/components/company/businessmodel/menu/BMMenu.vue";
import BMLoad from "@/components/company/businessmodel/load/BMLoad.vue";
import BMExample from "@/components/company/businessmodel/example/BMExample.vue";
import ExampleCanvas from "@/components/company/businessmodel/example/canvas/ExampleCanvas.vue";
import ProfileCreate from "@/components/company/profile/create/ProfileCreate.vue";
import Partners from "@/components/company/profile/partners/Partners.vue";
import MyPartners from "@/components/company/profile/partners/MyPartners.vue";
import KIRC from "@/components/company/kirc/KIRC.vue";
import KIRCRes from "@/components/company/kirc/result/KIRCRes.vue";
import KIRCCompare from "@/components/company/kirc/compare/kirccompare.vue";
import KIRC2 from "@/components/company/kirc/KIRC2.vue";
import KIRCRes2 from "@/components/company/kirc/result/KIRCRes2.vue";
import ResAdd from "@/components/company/kirc/result/ResAdd.vue";
import EventRecommendations from "@/components/company/profile/events/Events.vue";
import Confidentiality from "@/components/datasecurity/confidentiality/Confidentiality.vue";
import Impressum from "@/components/datasecurity/impressum/Impressum.vue";
import BusinessModelSearch from "@/components/search/businessmodelsearch/query/BusinessModelSearch.vue";
import ReferenceProjectSearch from "@/components/search/referenceprojectsearch/query/ReferenceProjectSearch.vue";
import EventSearch from "@/components/search/eventsearch/query/EventSearch.vue";
import KITrainerDetail from "@/components/search/kitrainersearch/detail/KITrainerDetail.vue";
import KITrainerSearch from "@/components/search/kitrainersearch/query/KITrainerSearch.vue";
import EventDetail from "@/components/search/eventsearch/detail/EventDetail.vue";
import BusinessModelDetail from "@/components/search/businessmodelsearch/detail/BusinessModelDetail.vue";
import CompanyDetail from "@/components/search/companysearch/detail/CompanyDetail.vue";
import ReferenceProjectDetail from "@/components/search/referenceprojectsearch/detail/ReferenceProjectDetail.vue";
import TechnologyDetail from "@/components/search/technologysearch/detail/TechnologyDetail.vue";
import OfferDetail from "@/components/search/offersearch/detail/OfferDetail.vue";

import Signin from "@/components/app/authentication/signin/Signin.vue";
import Activate from "@/components/app/authentication/signup/activate/Activate.vue";
import ActivateKI from "@/components/app/authentication/signup/activate/ActivateKI.vue";
import Signup from "@/components/app/authentication/signup/Signup.vue";
import SignupSuccess from "@/components/app/authentication/signup/success/SignupSuccess.vue";
import ForgotPass from "@/components/app/authentication/signin/forgotpass/ForgotPass.vue";
import NotFound from "@/components/landing/notfound/NotFound.vue";
import TechSearchMain from "@/components/search/techSearchNew/views/TechSearchMain.vue";
import OfferSearchMain from "@/components/search/offersearch/views/OfferSearchMain.vue";


/**
 * Add Router to project as middleware.
 */
Vue.use(Router);

/**
 * Requires a user authentification.
 * @param to - the target route being navigated to.
 * @param from - the current route being navigated away from.
 * @param next - action to resolve the hook.
 */
function requireAuth(to, from, next) {
  if (!auth.loggedIn()) {
    //redirect to login.
    next({
      path: "/login",
      query: { redirect: to.fullPath }
    });
  } else {
    // move on to the next hook in the pipeline.
    next();
  }
}

/**
 * Instantiation and configuration for the router.
 * @property {string} router.mode - The router's mode. If set to "history", the URL's hash is removed.
 * @property {object} router.routes - Definition of the routes.
 */
export default new Router({
  mode: "history",

  /**
   * @property {string} route.path - The path of the current route, always resolved as an absolute path.
   * @property {string} route.name - The name of the current route.
   * @property {component} route.component - The view component to be loaded for the current route.
   * @property {boolean} route.props - Used to decouple the component from the current router.
   * @property {function} route.beforeEnter - Per-route guard to be executed before the navigation is confirmed.
   * @property {*} route.meta - Arbitrary information attached to the current route.
   * @property {string} route.meta.transitionName - The name of the transition.
   * @property {string} route.meta.viewName - The name of the view.
   */
  routes: [
    /**
     * Welcome; Landing Page.
     */
    {
      path: "/",
      name: "Welcome",
      component: Landing,
      meta: { viewName: "Home" }
    },

    /**
     * Registration page.
     */
    {
      path: "/register",
      name: "Signup",
      component: Signup,
      meta: { viewName: "Registrierung" }
    },

    /**
     * Registration successful page.
     */
    {
      path: "/register/success",
      name: "SignupSuccess",
      component: SignupSuccess,
      meta: { viewName: "Registrierung erfolgreich" }
    },

    /**
     * Page to activate an account.
     * The hash is loaded dynamically.
     */
    {
      path: "/activate/:hash",
      name: "Activate",
      component: Activate,
      props: true,
      meta: { viewName: "Account aktivieren" }
    },

    /**
     * Page to active an AI trainer account.
     * The hash is loaded dynamically.
     */
    {
      path: "/activateki/:hash",
      name: "ActivateKI",
      component: ActivateKI,
      props: true,
      meta: { viewName: "Account aktivieren" }
    },

    /**
     * Login page.
     */
    {
      path: "/login",
      name: "Signin",
      component: Signin,
      meta: { viewName: "Anmeldung" }
    },

    /**
     * Logout page.
     */
    {
      path: "/logout",
      name: "Logout",
      beforeEnter(to, from, next) {
        //log the user out.
        auth.logout();
        //return to the welcome page.
        next("/");
      }
    },

    /**
     * Personal settings page.
     */
    {
      path: "/settings",
      name: "Settings",
      component: Settings,
      beforeEnter: requireAuth,
      meta: { viewName: "Einstellungen" }
    },

    /**
     * Forgot password(password reset) page.
     * The hash is loaded dynamically.
     */
    {
      path: "/forgot/:hash",
      name: "ForgotPass",
      component: ForgotPass,
      props: true,
      meta: { viewName: "Passwortreset" }
    },

    /**
     * "Not Found" error page.
     */
    {
      path: "/notFound",
      name: "NotFound",
      component: NotFound,
      meta: { viewName: "" }
    },

    /**
     * News page.
     */
    {
      path: "/news",
      name: "News",
      component: News,
      meta: { viewName: "Neuigkeiten" }
    },

    /**
     * Confidentiality information page.
     */
    {
      path: "/confidentiality",
      name: "Confidentiality",
      component: Confidentiality,
      meta: { viewName: "Datenschutz" }
    },

    /**
     * Legal disclosure page.
     */
    {
      path: "/impressum",
      name: "Impressum",
      component: Impressum,
      meta: { viewName: "Impressum" }
    },

    /**
     * Central page for the current business model; overview of the canvas.
     * The hash is loaded dynamically.
     */
    {
      path: "/businessmodel/:hash",
      name: "BusinessModel",
      component: BusinessModel,
      beforeEnter: requireAuth,
      props: true,
      meta: { transitionName: "", viewName: "Geschäftsmodell" }
    },

    /**
     * Menu, central page for the business model configurator.
     */
    {
      path: "/bm/menu",
      name: "BMMenu",
      component: BMMenu,
      beforeEnter: requireAuth,
      meta: { transitionName: "", viewName: "Geschäftsmodelle" }
    },

    /**
     * Page to list the existing business models of a user.
     */
    {
      path: "/bm/load",
      name: "BMLoad",
      component: BMLoad,
      beforeEnter: requireAuth,
      meta: { transitionName: "", viewName: "Geschäftsmodelle" }
    },

    /**
     * Page to list the exemplary business models.
     */
    {
      path: "/bm/examples",
      name: "BMExample",
      component: BMExample,
      beforeEnter: requireAuth,
      meta: { transitionName: "", viewName: "Geschäftsmodellbeispiele" }
    },

    /**
     * Current exemplary business model page.
     * The hash (name of the business model) is loaded dynamically.
     */
    {
      path: "/bm/examples/:bmname",
      name: "ExampleCanvas",
      props: true,
      component: ExampleCanvas,
      beforeEnter: requireAuth,
      meta: { transitionName: "", viewName: "Geschäftsmodellbeispiel" }
    },

    /**
     * Current canvas brick page for the current business model.
     * The hash and the name are loaded dynamically.
     */
    {
      path: "/businessmodel/:hash/brick/:name",
      name: "Brick",
      props: true,
      component: Brick,
      beforeEnter: requireAuth,
      meta: { transitionName: "zoom", viewName: "Geschäftsmodell" }
    },

    /**
     * Page to create the company profile.
     */
    {
      path: "/profile/create",
      name: "ProfileCreate",
      component: ProfileCreate,
      beforeEnter: requireAuth,
      meta: { viewName: "Unternehmensprofil" }
    },

    /**
     * Event recommendations page.
     */
    {
      path: "/profile/events",
      name: "EventRecommendations",
      component: EventRecommendations,
      beforeEnter: requireAuth,
      meta: { viewName: "Veranstaltungsempfehlungen" }
    },

    /**
     * Potential project partners page.
     */
    {
      path: "/profile/partners",
      name: "Partners",
      component: Partners,
      beforeEnter: requireAuth,
      meta: { viewName: "Potenzielle Projektpartner" }
    },

    /**
     * Selected potential project partners page.
     */
    {
      path: "/profile/partners/mypartners",
      name: "MyPartners",
      component: MyPartners,
      beforeEnter: requireAuth,
      meta: { viewName: "Ausgewählte Projektpartner" }
    },

    /**
     * AI readiness check welcome page.
     */
    {
      path: "/readiness-welcome",
      name: "kircLanding",
      component: kircLanding,
      meta: { viewName: "KI-Readiness-Check" }
    },

    /**
     * That was:
     * {
      path: "/readinesscheck",
      name: "Readinesscheck",
      component: KIRC,
      meta: { viewName: "KI-Readiness-Check" }

    },
*/
    {
      path: "/readinesscheck/resold/:code/:showResult",
      name: "KIRCResOld",
      component: KIRCRes,
      props: true,
      meta: { viewName: "KI-Readiness-Check (alte Version)" }
    },
     

    /**
     * AI readiness check tool page.
     */
    {
      path: "/readinesscheck",
      name: "Readinesscheck",
      component: KIRC2,
      meta: { viewName: "KI-Readiness-Check" }
    },

    /**
     * Page to access and view the results of the AI readiness check.
     */
    {
      path: "/readinesscheck/res",
      name: "KIRCRes",
      component: KIRCRes2,
      meta: { viewName: "KI-Readiness-Check" }
    },

    /**
     * Page to compare the results of the AI readiness check.
     */
    {
      path: "/readinesscheck/compare/:hash",
      name: "KIRCCompare",
      props: true,
      component: KIRCCompare,
      meta: { viewName: "KI-Readiness-Check" }
    },

    /**
     * Page to add a ki readiness check result to user using a hash code of a kirc result.
     */
    {
      path: "/readinesscheck/:hash",
      name: "ResAdd",
      component: ResAdd,
      beforeEnter: requireAuth,
      props: true,
      meta: { viewName: "Ergebis wird gesichert" }
    },

    /**
     * Page with a list of business model aspects (a.k.a. bricks in the canvas).
     */
    {
      path: "/search/businessmodel",
      name: "BusinessModelSearch",
      component: BusinessModelSearch,
      //beforeEnter: requireAuth,
      meta: { viewName: "Geschäftsmodellaspekte" }
    },

    /**
     * Page for searching for reference projects.
     */
    {
      path: "/search/referenceproject",
      name: "ReferenceProjectSearch",
      component: ReferenceProjectSearch,
      //beforeEnter: requireAuth,
      meta: { viewName: "Suche nach Referenzprojekten" }
    },

    /**
     * Page for searching for technologies.
     */
    {
      path: "/search/technology",
      name: "TechSearchMain",
      component: TechSearchMain,
      //beforeEnter: requireAuth,
      meta: { viewName: "Technologiesuche" }
    },


      /**
     * Page for searching for offers.
     */
       {
        path: "/search/offer",
        name: "OfferSearchMain",
        component: OfferSearchMain,
        //beforeEnter: requireAuth,
        meta: { viewName: "Technologiesuche" }
      },


    /**
     * Page for searching for events.
     */
    {
      path: "/search/event",
      name: "EventSearch",
      component: EventSearch,
      //beforeEnter: requireAuth,
      meta: { viewName: "Veranstaltungssuche" }
    },

    /**
     * Page for searching for AI trainers.
     */
    {
      path: "/search/kitrainer",
      name: "KITrainerSearch",
      component: KITrainerSearch,
      //beforeEnter: requireAuth,
      meta: { viewName: "KI-Trainersuche" }
    },

    /**
     * Page with details on the current business model.
     */
    {
      path: "/businessmodeldetail/:hash",
      name: "BusinessModelDetail",
      props: true,
      component: BusinessModelDetail
    },

    /**
     * Page with details on the current company.
     * The hash is loaded dynamically.
     */
    {
      path: "/companydetail/:hash",
      name: "CompanyDetail",
      props: true,
      component: CompanyDetail,
      beforeEnter: requireAuth,
      meta: { viewName: "Unternehmenssuche" }
    },

    /**
     * Page with details on the current reference project.
     * The hash is loaded dynamically.
     */
    {
      path: "/referenceprojectdetail/:hash",
      name: "ReferenceProjectDetail",
      props: true,
      component: ReferenceProjectDetail,
      //beforeEnter: requireAuth,
      meta: { viewName: "Suche nach Referenzprojekten" }
    },

    /**
     * Page with details on the current technology.
     * The hash is loaded dynamically.
     */
    {
      path: "/technologydetail/:hash",
      name: "TechnologyDetail",
      props: true,
      component: TechnologyDetail,
      //beforeEnter: requireAuth,
      meta: { viewName: "Technologiesuche" }
    },

      /**
     * Page with details on the current technology. 
     * The hash is loaded dynamically.
     */
       {
        path: "/offerdetail/:hash",
        name: "OfferDetail",
        props: true,
        component: OfferDetail,
        //beforeEnter: requireAuth,
        meta: { viewName: "Angebotssuche" }
      },

    /**
     * Page with details on the current event.
     * The hash is loaded dynamically.
     */
    {
      path: "/eventdetail/:hash",
      name: "EventDetail",
      props: true,
      component: EventDetail,
      //beforeEnter: requireAuth,
      meta: { viewName: "Veranstaltungssuche" }
    },

    /**
     * Page with details on the current AI trainer.
     * The hash is loaded dynamically.
     */
    {
      path: "/trainerdetail/:hash",
      name: "KITrainerDetail",
      props: true,
      component: KITrainerDetail,
      //beforeEnter: requireAuth,
      meta: { viewName: "KI-Trainersuche" }
    }
  ],

  /**
   * Scrolls to the top of the page for all route navigations.
   * @property {number} x - x coordinate on page.
   * @property {number} y - y coordinate on page.
   */
  scrollBehavior() {
    return { x: 0, y: 0 };
  }
});



// WEBPACK FOOTER //
// ./src/router/index.js